import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Table, Breadcrumb, Input } from 'antd';
import { MenuOutlined, SearchOutlined } from '@ant-design/icons';
import chunk from 'lodash.chunk';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useStore } from '../../../store';
import { IReportTree } from '../../../store/modules/report/i-report';
import breadcrumbSeparator from '../../../assets/icons/breadcrumbSeparator.svg';
import {
  CREATE_REPORT_PATH,
  // REPORTS_LIST_PATH, CREATE_REPORT_GROUP_PATH, EDIT_REPORT_PATH, MAP_REPORT_PATH,
  REPORTS_LIST_PATH, EDIT_REPORT_PATH, MAP_REPORT_PATH,
} from '../../routing/paths.constants';
import { useActions } from '../../providers/actions-provider/actions.context';
import BreadcrumbItem from '../../lib/breadcrumbItem';
import PageTitle from '../../lib/page-title';
import PrimaryButton from '../../lib/primary-button';
import SecondaryButton from '../../lib/secondary-button';
import FilterSelect from '../../lib/filter-select';
import findReportInTheTree from './findReportInTheTree.utils';
import './reports.less';
import PermissionProvider from '../../providers/permissionProvider';
import checkPermission from '../../../utils/permissions/permissions';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';

interface ICrumb {
  path: string;
  breadcrumbName: string;
}
const Reports = (): JSX.Element => {
  const { reportsTree } = useStore();
  const { reportActions } = useActions();
  const { location, push, location: { pathname } } = useHistory();
  const name = location.pathname.split('/').splice(-2, 1).pop() || '';
  const [currentFilter, setCurrentFilter] = useState<string>('name');
  const [searchValue, setSearchValue] = useState<string>('');
  const [finalReports, setFinalReports] = useState<Array<IReportTree>>([]);
  const [copyReports, setCopyReports] = useState<Array<IReportTree>>([]);
  const hasNotWritePermission = !checkPermission('reports', 'write');
  const { facilityActions } = useActions();
  const localStorageService = useLocalStorage();

  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    facilityActions.resetMapValues();
    facilityActions.resetMapReportValues();
    reportActions.resetExecutedReport();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // eslint-disable-next-line
  function handleDragStart(event: any) {
    const { active } = event;
    setActiveId(active.id);
  }

  // eslint-disable-next-line
  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const reordered = reportActions.swapReportsPositions(copyReports, active.id, over.id);
      setCopyReports(reordered);
    }
    // Stop overlay.
    setActiveId(null);
  }

  const onSearch = (e: string) => {
    const filteredReports = finalReports.filter((finalReport) => {
      const copyAsset = JSON.parse(JSON.stringify(finalReport));
      if (!currentFilter || !copyAsset[currentFilter]) {
        return finalReport;
      }
      if (copyAsset[currentFilter].toLowerCase().indexOf(e.toLowerCase()) > -1) {
        return finalReport;
      }
      return null;
    });
    setCopyReports([...filteredReports]);
  };

  const handleChange = (e: string) => {
    setCurrentFilter(e);
    onSearch(searchValue);
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    onSearch(e.target.value);
  };

  const isReportsListPage = location.pathname === REPORTS_LIST_PATH;
  const resolvedTitle = isReportsListPage
    ? `Reports${reportsTree.loading ? '' : ` (${copyReports.length} total)`}`
    : name;

  let dataForCrumbs: ICrumb[] = [];
  const initialCrumb = {
    path: REPORTS_LIST_PATH,
    breadcrumbName: 'Reports List',
  };
  if (location.pathname === REPORTS_LIST_PATH) {
    dataForCrumbs.push(initialCrumb);
  } else {
    // preparing routes for creation crumbs - without first two static '' and 'reports-list'
    const prepareRoutes = location.pathname.split('/').slice(2);
    // break array by chunk of /name-of-nest and /id
    const chunks = chunk(prepareRoutes, 2);
    let path = REPORTS_LIST_PATH;
    const crumbsWithoutInitial = chunks.map((el) => {
      // each next path need to include previous
      path += `/${el[0]}/${el[1]}`;
      return {
        path,
        breadcrumbName: el[0],
      };
    });
    dataForCrumbs = [initialCrumb, ...crumbsWithoutInitial];
  }

  useEffect(() => {
    reportActions.getReportsList();
  }, []);

  useEffect(() => {
    const copyChildReports: IReportTree[] = [];
    let copyParentReports: IReportTree[] = [];

    if (name && `/${name}` !== REPORTS_LIST_PATH) {
      const result = findReportInTheTree(reportsTree, 'name', name);

      if (result[0]?.children && result[0]?.children.length) {
        result[0].children.forEach((el) => {
          if (el.type === 'group') {
            copyChildReports.push(el);
          } else if (el.type === 'report') {
            copyParentReports.push(el);
          }
        });
      } else if (!result[0]?.children) {
        copyParentReports = result;
      }
    } else {
      reportsTree.data.forEach((el) => {
        if (el.type === 'group') {
          copyChildReports.push(el);
        } else if (el.type === 'report') {
          copyParentReports.push(el);
        }
      });
    }
    setFinalReports(copyParentReports);
    setCopyReports(copyParentReports);
  }, [pathname]);

  useEffect(() => {
    const copyChildReports: IReportTree[] = [];
    let copyParentReports: IReportTree[] = [];
    if (name && `/${name}` !== REPORTS_LIST_PATH) {
      const result = findReportInTheTree(reportsTree, 'name', name);
      if (result[0]?.children && result[0]?.children.length) {
        result[0].children.forEach((el) => {
          if (el.type === 'group') {
            copyChildReports.push(el);
          } else if (el.type === 'report') {
            copyParentReports.push(el);
          }
        });
      } else if (!result[0]?.children) {
        copyParentReports = result;
      }
    } else {
      reportsTree.data.forEach((el) => {
        if (el.type === 'group') {
          copyChildReports.push(el);
        } else if (el.type === 'report') {
          copyParentReports.push(el);
        }
      });
    }
    setFinalReports(copyParentReports);
    setCopyReports(copyParentReports);
  }, [reportsTree]);

  const goToCreation = () => {
    // slice string after /reports-list prefix
    push(`${REPORTS_LIST_PATH}${CREATE_REPORT_PATH}${location.pathname.slice(13)}`);
  };

  const filters = [
    {
      name: 'Name',
      value: 'name',
    },
    {
      name: 'Description',
      value: 'description',
    },
    {
      name: 'Code',
      value: 'code',
    },
  ];
  const columns = [
    {
      key: 'dragHandle',
      dataIndex: 'dragHandle',
      title: 'Drag',
      width: 30,
      render: () => <MenuOutlined />,
    },
    {
      title: 'Name',
      key: 'name',
      dataIndex: 'name',
      sorter: (a: IReportTree, b: IReportTree) => a.name.localeCompare(b.name),
    },
    {
      title: 'Code',
      key: 'code',
      dataIndex: 'code',
      sorter: (a: IReportTree, b: IReportTree) => a.code.localeCompare(b.code),
    },
    {
      title: 'Collection',
      key: 'collection',
      dataIndex: 'collection',
      sorter: (a: IReportTree, b: IReportTree) => a.collection.localeCompare(b.collection),
    },
    {
      title: 'Description',
      key: 'description',
      dataIndex: 'description',
      sorter: (a: IReportTree, b: IReportTree) => a.description.localeCompare(b.description),
    },
    {
      title: '',
      key: 'copy',
      dataIndex: 'copy',
      width: 50,
      // eslint-disable-next-line react/display-name
      render: (text: string, record: IReportTree) => (
        !hasNotWritePermission && (
          <SecondaryButton
            type="button"
            className="button-execute ant-btn--small"
            onClick={async () => {
              const copiedReport = await reportActions.copyReport(record.id);
              if (copiedReport) push(`${REPORTS_LIST_PATH}${EDIT_REPORT_PATH}/exec-${copiedReport.id}${location.pathname.slice(13)}/base`);
              reportActions.getReportsList();
            }}
          >
            copy
          </SecondaryButton>
        )
      ),
    },
    {
      title: '',
      key: 'edit',
      dataIndex: 'edit',
      width: 50,
      // eslint-disable-next-line react/display-name
      render: (text: string, record: IReportTree) => (
        <SecondaryButton
          type="button"
          className="button-execute ant-btn--small"
          onClick={() => {
            push(`${REPORTS_LIST_PATH}${EDIT_REPORT_PATH}/exec-${record.id}${location.pathname.slice(13)}/base`);
          }}
        >
          {hasNotWritePermission ? 'view' : 'edit'}
        </SecondaryButton>
      ),
    },
    {
      title: '',
      key: 'execute',
      dataIndex: 'execute',
      width: 50,
      // eslint-disable-next-line react/display-name
      render: (text: string, record: IReportTree) => (
        <SecondaryButton
          type="button"
          className="button-execute ant-btn--small"
          onClick={() => {
            push(`${REPORTS_LIST_PATH}/exec-${record.id}${location.pathname.slice(13)}`);
          }}
        >
          execute
        </SecondaryButton>
      ),
    },
    {
      title: '',
      key: 'map',
      dataIndex: 'map',
      width: 50,
      // eslint-disable-next-line react/display-name
      render: (text: string, record: IReportTree) => (
        <SecondaryButton
          type="button"
          disabled={record.mappable === null || !record.mappable}
          className={`button-execute ant-btn--small ${record.mappable ? '' : 'disabled'}`}
          onClick={() => {
            push(`${REPORTS_LIST_PATH}${MAP_REPORT_PATH}/exec-${record.id}/${record.code}`);
          }}
        >
          Map
        </SecondaryButton>
      ),
    },
  ];

  // eslint-disable-next-line
  function DraggableWrapper(props: any) {
    const { children, ...restProps } = props;
    /**
     * 'children[1]` is `dataSource`
     * Check if `children[1]` is an array
     * because antd gives 'No Data' element when `dataSource` is an empty array
     */
    return (
      <SortableContext
        items={
          children[1] instanceof Array
            ? children[1].map((child) => child.key)
            : []
        }
        strategy={verticalListSortingStrategy}
        // eslint-disable-next-line
        {...restProps}
      >
        {/* eslint-disable-next-line */}
        <tbody {...restProps}>
          {
            // This invokes `Table.components.body.row` for each element of `children`.
            children
          }
        </tbody>
      </SortableContext>
    );
  }

  // eslint-disable-next-line
  function DraggableRow(props: any) {
    const {
      attributes,
      listeners,
      setNodeRef,
      isDragging,
      overIndex,
      index,
    } = useSortable({
      // eslint-disable-next-line
      id: props['data-row-key'],
    });
    const isOver = overIndex === index;
    const { children, ...restProps } = props;
    const isData = children instanceof Array;
    const style = {
      ...restProps?.style,
      ...(isData && isDragging ? { background: '#80808038' } : {}),
      ...(isData && isOver ? { borderTop: '5px solid #ec161638' } : {}),
    };
    /**
     * 'children[1]` is a row of `dataSource`
     * Check if `children[1]` is an array
     * because antd gives 'No Data' element when `dataSource` is an empty array
    */
    return (
      // eslint-disable-next-line
      <tr ref={setNodeRef} {...attributes} {...restProps} style={style}>
        {children instanceof Array
          ? children.map((child) => {
            // eslint-disable-next-line
            const { key, children, $$typeof, ...restProps } = child;
            return key === 'dragHandle' ? (
              // eslint-disable-next-line
              <td key={key} {...listeners} {...restProps} className='ant-table-cell'>
                {React.createElement('span', { ...restProps, ...listeners }, <MenuOutlined />)}
              </td>
            ) : (
              child
            );
          })
          : children}
      </tr>
    );
  }

  return (
    <div className="execute-report__wrapper">
      <PermissionProvider pageId="reports">
        <Breadcrumb
          itemRender={BreadcrumbItem}
          routes={dataForCrumbs}
          separator={<img src={breadcrumbSeparator} alt="breadcrumb separator" />}
          params={{ dataForCrumbs }}
        />
        <PageTitle back={!isReportsListPage} title={resolvedTitle}>
          <div>
            {/* <SecondaryButton
              title="New Folder"
              onClick={() => push(`${location.pathname}${CREATE_REPORT_GROUP_PATH}`)}
              onClick={() => push(`${location.pathname}${CREATE_REPORT_GROUP_PATH}`)}
              className="left"
              disabled={hasNotWritePermission}
            /> */}
            { !hasNotWritePermission && <PrimaryButton title="New Report" onClick={goToCreation} />}
          </div>
        </PageTitle>
        <div className="table__wrapper">
          <div className="filter__wrapper">
            <FilterSelect
              currentFilter={currentFilter}
              defaultValue="name"
              onChange={(e) => handleChange(e)}
              data={filters}
            />
            <Input
              className="search"
              placeholder="Keyword"
              onChange={onChange}
              suffix={<SearchOutlined style={{ color: '#D0D3D9' }} />}
            />
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Table
              className="table"
              columns={columns}
              dataSource={copyReports}
              rowKey={(record) => record.id}
              bordered={false}
              loading={reportsTree.loading}
              pagination={{ position: ['bottomCenter'], hideOnSinglePage: true, defaultPageSize: 100 }}
              components={{
                body: {
                  wrapper: DraggableWrapper,
                  row: DraggableRow,
                },
              }}
            />
            <DragOverlay>
              <Table
                columns={columns}
                showHeader={false}
                rowKey="id"
                dataSource={
                  activeId
                    ? new Array(1).fill(
                      copyReports[
                        copyReports.findIndex((item) => item.id === activeId)
                      ],
                    )
                    : []
                }
                pagination={false}
              />
            </DragOverlay>
          </DndContext>
        </div>
      </PermissionProvider>
    </div>
  );
};

Reports.displayName = 'Reports';
export default Reports;
